import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "../components/ui/button";
import {
  answerButtonFunction,
  callButtonFunction,
  cameraStream,
  hangUp,
  NewAdminPermissionsStream,
  NewParticipantPermissionsStream,
  onCallEnded,
  onConnectionStateChange,
} from "../components/webrtc/connections";
import {
  Eye,
  EyeOff,
  Loader2,
  Maximize2,
  Mic,
  MicOff,
  Minimize2,
  MonitorUp,
  PhoneOff,
  Video,
  VideoOff,
} from "lucide-react";
import Draggable from "react-draggable";
import { animateFriendState } from "../recoilState/animateFriendState";
import { useRecoilState } from "recoil";
import { sendAnimateNotification } from "../actions/send-animate-notification";
import { useNavigate } from "react-router-dom";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import Seo from "../components/Seo";

// Call lifecycle phases
// ready      -> admin landing screen, waiting for the Start click
// starting   -> acquiring media / creating the call
// waiting    -> admin: invite sent, waiting for the friend to join
// connecting -> participant: answering and negotiating the connection
// connected  -> live call
// error      -> something failed, retry available
// ended      -> the other side hung up

const AniMate = () => {
  const navigate = useNavigate();
  const urlParams = new URLSearchParams(window.location.search);
  const isAdmin = urlParams.get("admin") === "true";
  const callIdParam = urlParams.get("callId");
  const hasParams = urlParams.size !== 0;

  const [friend, setFriend] = useRecoilState(animateFriendState);
  const [phase, setPhase] = useState(isAdmin ? "ready" : "connecting");
  const [errorMessage, setErrorMessage] = useState("");
  const [connectionLost, setConnectionLost] = useState(false);

  const [visibilitySelf, setvisibilitySelf] = useState(true);
  const [visibilityFriend, setvisibilityFriend] = useState(true);
  const [micOn, setmicOn] = useState(true);
  const [videoSharing, setvideoSharing] = useState(true);
  const [fullScreen, setfullScreen] = useState(false);
  const [isButtonsVisible, setIsButtonsVisible] = useState(true);

  const webcamVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const shareVideoRef = useRef(null);
  const selfTileRef = useRef(null);
  const friendTileRef = useRef(null);
  const timeoutRef = useRef(null);
  const friendRef = useRef(friend);
  friendRef.current = friend ?? friendRef.current;

  const inCall =
    phase === "waiting" || phase === "connecting" || phase === "connected";

  // ---- controls auto-hide -------------------------------------------------
  const handleActivity = useCallback(() => {
    setIsButtonsVisible(true);
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setIsButtonsVisible(false), 3500);
  }, []);

  useEffect(() => {
    timeoutRef.current = setTimeout(() => setIsButtonsVisible(false), 3500);
    window.addEventListener("mousemove", handleActivity);
    window.addEventListener("keydown", handleActivity);
    return () => {
      clearTimeout(timeoutRef.current);
      window.removeEventListener("mousemove", handleActivity);
      window.removeEventListener("keydown", handleActivity);
    };
  }, [handleActivity]);

  // ---- call lifecycle -----------------------------------------------------
  const registerCallbacks = useCallback(() => {
    onConnectionStateChange((state) => {
      if (state === "connected") {
        setConnectionLost(false);
        setPhase("connected");
      } else if (state === "disconnected") {
        setConnectionLost(true);
      } else if (state === "failed") {
        setErrorMessage(
          "The connection failed. This can happen on restrictive networks — try again or switch networks.",
        );
        setPhase("error");
      }
    });
    onCallEnded(() => {
      setPhase((p) => (p === "error" ? p : "ended"));
    });
  }, []);

  const startAdminSession = useCallback(async () => {
    const invitee = friendRef.current;
    if (!invitee) {
      setErrorMessage(
        "This invite session has expired. Go back home and invite your friend again.",
      );
      setPhase("error");
      return;
    }
    setPhase("starting");
    setErrorMessage("");
    try {
      registerCallbacks();
      const { screenStream, cameraStream, remoteStream } =
        await NewAdminPermissionsStream();
      if (webcamVideoRef.current)
        webcamVideoRef.current.srcObject = cameraStream;
      if (remoteVideoRef.current)
        remoteVideoRef.current.srcObject = remoteStream;
      if (shareVideoRef.current) shareVideoRef.current.srcObject = screenStream;
      const cid = await callButtonFunction();
      await sendAnimateNotification(invitee, cid);
      setPhase("waiting");
    } catch (error) {
      console.error("Failed to start AniMate session:", error);
      setErrorMessage(
        error?.message || "Something went wrong while starting the session.",
      );
      setPhase("error");
    }
  }, [registerCallbacks]);

  const startParticipantSession = useCallback(async () => {
    if (!callIdParam) {
      setErrorMessage(
        "This call link is missing its call id. Ask your friend to invite you again.",
      );
      setPhase("error");
      return;
    }
    setPhase("connecting");
    setErrorMessage("");
    try {
      registerCallbacks();
      const { localStream, remoteScreenStream, remoteCameraStream } =
        await NewParticipantPermissionsStream();
      if (webcamVideoRef.current)
        webcamVideoRef.current.srcObject = localStream;
      if (remoteVideoRef.current)
        remoteVideoRef.current.srcObject = remoteCameraStream;
      if (shareVideoRef.current)
        shareVideoRef.current.srcObject = remoteScreenStream;
      await answerButtonFunction(callIdParam);
    } catch (error) {
      console.error("Failed to join AniMate session:", error);
      setErrorMessage(
        error?.message || "Something went wrong while joining the session.",
      );
      setPhase("error");
    }
  }, [callIdParam, registerCallbacks]);

  useEffect(() => {
    if (!hasParams) return undefined;
    // The admin starts via an explicit click: getDisplayMedia requires a fresh
    // user gesture and intermittently fails when fired from a navigation effect.
    if (!isAdmin) {
      startParticipantSession();
    }
    return () => {
      hangUp();
      setFriend(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- media controls -----------------------------------------------------
  const toggleVideo = () => {
    const videoTrack = cameraStream?.getVideoTracks()[0];
    if (videoTrack) {
      videoTrack.enabled = !videoTrack.enabled;
      setvideoSharing(videoTrack.enabled);
    }
  };

  const toggleMic = () => {
    const audioTrack = cameraStream?.getAudioTracks()[0];
    if (audioTrack) {
      audioTrack.enabled = !audioTrack.enabled;
      setmicOn(audioTrack.enabled);
    }
  };

  const handleFullscreenToggle = () => {
    if (!fullScreen) {
      document.documentElement.requestFullscreen?.().catch(() => {});
      setfullScreen(true);
    } else {
      document.exitFullscreen?.().catch(() => {});
      setfullScreen(false);
    }
  };

  async function handleHangUp() {
    await hangUp();
    if (document.fullscreenElement) document.exitFullscreen?.().catch(() => {});
    navigate("/");
  }

  const goHome = () => {
    navigate("/");
  };

  const controlsClassName = `${
    isButtonsVisible || phase !== "connected"
      ? "opacity-100 translate-y-0"
      : "opacity-0 translate-y-4 pointer-events-none"
  } transition-all duration-300`;

  const controlButtonClass =
    "h-11 w-11 rounded-full flex items-center justify-center transition-colors";

  if (!hasParams) {
    return (
      <div className="h-screen w-screen bg-zinc-950 text-white flex flex-col items-center justify-center gap-4">
        <div className="modern-font text-3xl font-semibold">AniMate</div>
        <p className="text-zinc-400">This session link is invalid.</p>
        <Button
          onClick={goHome}
          className="rounded-full bg-white text-black hover:bg-zinc-200"
        >
          Back to home
        </Button>
      </div>
    );
  }

  const friendName = friendRef.current?.name || "your friend";

  return (
    <div className="h-screen w-screen bg-zinc-950 text-white overflow-hidden relative select-none">
      <Seo
        path="/animate"
        title="AniMate — Watch Anime & Study Together"
        description="AniMate lets you watch anime and screen share with friends while you study on Otakufy. Take your Animedoro anime breaks together in real time."
      />
      {/* Screen share — always mounted so srcObject can attach at any time */}
      <video
        ref={shareVideoRef}
        className="absolute inset-0 w-full h-full object-contain"
        autoPlay
        playsInline
        muted={isAdmin}
      ></video>

      {/* ---- status overlays ---- */}
      {phase === "ready" && (
        <div className="absolute inset-0 z-30 bg-zinc-950 flex items-center justify-center p-4">
          <div className="w-full max-w-md rounded-2xl border border-zinc-800 bg-zinc-900/80 backdrop-blur p-8 flex flex-col items-center gap-5 text-center">
            <div className="modern-font text-3xl font-semibold">AniMate</div>
            {friendRef.current && (
              <div className="flex items-center gap-3">
                <Avatar className="h-12 w-12 border-2 border-zinc-700">
                  <AvatarImage src={friendRef.current.imageurl} />
                  <AvatarFallback className="bg-zinc-800 text-white font-bold">
                    {friendRef.current.name?.[0]}
                  </AvatarFallback>
                </Avatar>
                <div className="text-left">
                  <div className="font-semibold">{friendRef.current.name}</div>
                  <div className="text-sm text-zinc-400">will be invited</div>
                </div>
              </div>
            )}
            <p className="text-sm text-zinc-400">
              You'll be asked to pick a screen or tab to share, then your camera
              and mic. Your friend gets the invite once sharing starts.
            </p>
            <div className="flex gap-3">
              <Button
                onClick={goHome}
                variant="outline"
                className="rounded-full border-zinc-700 bg-transparent text-white hover:bg-zinc-800 hover:text-white"
              >
                Cancel
              </Button>
              <Button
                onClick={startAdminSession}
                className="rounded-full bg-white text-black hover:bg-zinc-200 gap-2"
              >
                <MonitorUp size={18} />
                Start sharing
              </Button>
            </div>
          </div>
        </div>
      )}

      {phase === "starting" && (
        <div className="absolute inset-0 z-30 bg-zinc-950/90 flex flex-col items-center justify-center gap-4">
          <Loader2 className="animate-spin text-zinc-300" size={36} />
          <p className="text-zinc-300">Setting up your streams…</p>
        </div>
      )}

      {phase === "connecting" && (
        <div className="absolute inset-0 z-30 bg-zinc-950/90 flex flex-col items-center justify-center gap-4">
          <Loader2 className="animate-spin text-zinc-300" size={36} />
          <p className="text-zinc-300">Joining the session…</p>
          <p className="text-sm text-zinc-500">
            Allow camera and microphone access if prompted.
          </p>
        </div>
      )}

      {phase === "error" && (
        <div className="absolute inset-0 z-30 bg-zinc-950 flex items-center justify-center p-4">
          <div className="w-full max-w-md rounded-2xl border border-red-900/60 bg-zinc-900/80 backdrop-blur p-8 flex flex-col items-center gap-5 text-center">
            <div className="text-xl font-semibold">Something went wrong</div>
            <p className="text-sm text-zinc-400">{errorMessage}</p>
            <div className="flex gap-3">
              <Button
                onClick={goHome}
                variant="outline"
                className="rounded-full border-zinc-700 bg-transparent text-white hover:bg-zinc-800 hover:text-white"
              >
                Back to home
              </Button>
              {(isAdmin ? friendRef.current : callIdParam) && (
                <Button
                  onClick={() =>
                    isAdmin ? setPhase("ready") : startParticipantSession()
                  }
                  className="rounded-full bg-white text-black hover:bg-zinc-200"
                >
                  Try again
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {phase === "ended" && (
        <div className="absolute inset-0 z-30 bg-zinc-950 flex flex-col items-center justify-center gap-4">
          <div className="text-2xl font-semibold">Call ended</div>
          <p className="text-zinc-400">The AniMate session is over.</p>
          <Button
            onClick={goHome}
            className="rounded-full bg-white text-black hover:bg-zinc-200"
          >
            Back to home
          </Button>
        </div>
      )}

      {/* ---- in-call banners ---- */}
      {phase === "waiting" && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2 rounded-full bg-zinc-900/80 backdrop-blur border border-zinc-700 px-4 py-2 text-sm">
          <span className="relative flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-amber-400"></span>
          </span>
          Invite sent — waiting for {friendName} to join…
        </div>
      )}

      {phase === "connected" && connectionLost && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2 rounded-full bg-red-950/80 backdrop-blur border border-red-800 px-4 py-2 text-sm">
          <Loader2 className="animate-spin" size={14} />
          Connection lost — trying to reconnect…
        </div>
      )}

      {/* ---- PiP tiles ---- */}
      <Draggable
        nodeRef={selfTileRef}
        bounds="parent"
        defaultPosition={{ x: 16, y: 16 }}
      >
        <div
          ref={selfTileRef}
          className={`absolute z-10 cursor-grab active:cursor-grabbing group ${
            inCall && visibilitySelf ? "" : "hidden"
          }`}
        >
          <div className="relative rounded-xl overflow-hidden border border-zinc-700 shadow-xl shadow-black/50 bg-zinc-900">
            <video
              ref={webcamVideoRef}
              className={`w-[140px] sm:w-[190px] md:w-[220px] lg:w-[260px] h-auto block ${
                videoSharing ? "" : "opacity-30"
              }`}
              autoPlay
              playsInline
              muted
            ></video>
            <div className="absolute bottom-1.5 left-1.5 flex items-center gap-1.5 rounded-md bg-black/60 px-2 py-0.5 text-xs">
              You
              {!micOn && <MicOff size={11} className="text-red-400" />}
            </div>
            <button
              onClick={() => setvisibilitySelf(false)}
              className="absolute top-1.5 right-1.5 rounded-md bg-black/60 p-1 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/80"
              title="Hide your tile"
            >
              <EyeOff size={13} />
            </button>
          </div>
        </div>
      </Draggable>

      <Draggable
        nodeRef={friendTileRef}
        bounds="parent"
        defaultPosition={{ x: 16, y: 220 }}
      >
        <div
          ref={friendTileRef}
          className={`absolute z-10 cursor-grab active:cursor-grabbing group ${
            phase === "connected" && visibilityFriend ? "" : "hidden"
          }`}
        >
          <div className="relative rounded-xl overflow-hidden border border-zinc-700 shadow-xl shadow-black/50 bg-zinc-900">
            <video
              ref={remoteVideoRef}
              className="w-[140px] sm:w-[190px] md:w-[220px] lg:w-[260px] h-auto block"
              autoPlay
              playsInline
            ></video>
            <div className="absolute bottom-1.5 left-1.5 rounded-md bg-black/60 px-2 py-0.5 text-xs">
              {isAdmin ? friendName : "Friend"}
            </div>
            <button
              onClick={() => setvisibilityFriend(false)}
              className="absolute top-1.5 right-1.5 rounded-md bg-black/60 p-1 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/80"
              title="Hide friend tile"
            >
              <EyeOff size={13} />
            </button>
          </div>
        </div>
      </Draggable>

      {/* ---- bottom control bar ---- */}
      {inCall && (
        <div
          className={`absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2 rounded-full bg-zinc-900/80 backdrop-blur border border-zinc-700 px-3 py-2 ${controlsClassName}`}
        >
          <button
            onClick={toggleMic}
            title={micOn ? "Mute microphone" : "Unmute microphone"}
            className={`${controlButtonClass} ${
              micOn
                ? "bg-zinc-800 hover:bg-zinc-700"
                : "bg-red-600 hover:bg-red-500"
            }`}
          >
            {micOn ? <Mic size={19} /> : <MicOff size={19} />}
          </button>
          <button
            onClick={toggleVideo}
            title={videoSharing ? "Turn camera off" : "Turn camera on"}
            className={`${controlButtonClass} ${
              videoSharing
                ? "bg-zinc-800 hover:bg-zinc-700"
                : "bg-red-600 hover:bg-red-500"
            }`}
          >
            {videoSharing ? <Video size={19} /> : <VideoOff size={19} />}
          </button>
          <div className="w-px h-6 bg-zinc-700 mx-1"></div>
          <button
            onClick={() => setvisibilitySelf((p) => !p)}
            title={visibilitySelf ? "Hide your tile" : "Show your tile"}
            className={`${controlButtonClass} bg-zinc-800 hover:bg-zinc-700 ${
              visibilitySelf ? "" : "text-zinc-500"
            }`}
          >
            {visibilitySelf ? <Eye size={19} /> : <EyeOff size={19} />}
          </button>
          <button
            onClick={() => setvisibilityFriend((p) => !p)}
            title={visibilityFriend ? "Hide friend tile" : "Show friend tile"}
            className={`${controlButtonClass} bg-zinc-800 hover:bg-zinc-700 ${
              visibilityFriend ? "" : "text-zinc-500"
            }`}
          >
            {visibilityFriend ? <Eye size={19} /> : <EyeOff size={19} />}
          </button>
          <button
            onClick={handleFullscreenToggle}
            title={fullScreen ? "Exit fullscreen" : "Enter fullscreen"}
            className={`${controlButtonClass} bg-zinc-800 hover:bg-zinc-700`}
          >
            {fullScreen ? <Minimize2 size={19} /> : <Maximize2 size={19} />}
          </button>
          <div className="w-px h-6 bg-zinc-700 mx-1"></div>
          <button
            onClick={handleHangUp}
            title="Leave call"
            className={`${controlButtonClass} bg-red-600 hover:bg-red-500 px-6 w-auto`}
          >
            <PhoneOff size={19} />
          </button>
        </div>
      )}
    </div>
  );
};

export default AniMate;
